import React, { FC, useState, useEffect, useMemo, Key } from "react";
import { Invoice, ProductLine } from "../data/types";
import { initialInvoice, initialProductLine } from "../data/initialData";
import EditableInput from "./EditableInput";
import EditableSelect from "./EditableSelect";
import EditableTextarea from "./EditableTextarea";
import EditableCalendarInput from "./EditableCalendarInput";
import EditableFileImage from "./EditableFileImage";
import countryList from "../data/countryList";
import Document from "./Document";
import Page from "./Page";
import View from "./View";
import Text from "./Text";
import { Font } from "@react-pdf/renderer";
import Download from "./DownloadPDF";
import { format } from "date-fns/format";
import { parse } from "date-fns/parse";
import { isValid } from "date-fns/isValid";
import {
  Button,
  Modal,
  Box,
  TextareaAutosize,
  IconButton,
  List,
  ListItemText,
  TextField,
  ListItemButton,
  Chip,
} from "@mui/material";
import { auth, db } from "../../../firebase";
import { collection, getDocs, query } from "firebase/firestore";
import { StockItem } from "../../StockManagement";
import SearchIcon from "@mui/icons-material/Search";

Font.register({
  family: "Nunito",
  fonts: [
    { src: "https://fonts.gstatic.com/s/nunito/v12/XRXV3I6Li01BKofINeaE.ttf" },
    {
      src: "https://fonts.gstatic.com/s/nunito/v12/XRXW3I6Li01BKofA6sKUYevN.ttf",
      fontWeight: 600,
    },
  ],
});

interface Props {
  data?: Invoice;
  pdfMode?: boolean;
  onChange?: (invoice: Invoice) => void;
  useStock?: boolean;
  isEditMode?: boolean;
}

const InvoicePage: FC<Props> = ({
  data,
  pdfMode,
  onChange,
  useStock = false,
  isEditMode = false,
}) => {
  const [invoice, setInvoice] = useState<Invoice>(
    data ? { ...data } : { ...initialInvoice },
  );
  const [customColumns, setCustomColumns] = useState<string[]>([]);
  const [inputData, setInputData] = useState<string>("");
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [openStockSelector, setOpenStockSelector] = useState(false);
  const [selectedLineIndex, setSelectedLineIndex] = useState<number | null>(
    null,
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredStockItems, setFilteredStockItems] = useState<StockItem[]>([]);
  const [selectedStockPerLine, setSelectedStockPerLine] = useState<{
    [key: number]: StockItem | null;
  }>({});

  useEffect(() => {
    // Load stock items if stock feature is enabled
    if (useStock) {
      const fetchStockItems = async () => {
        const user = auth.currentUser;
        if (!user) {
          console.log("No user authenticated");
          return;
        }

        try {
          const itemsCollection = collection(
            db,
            "stockItems",
            user.uid,
            "userStockItems",
          );
          const itemsSnapshot = await getDocs(query(itemsCollection));
          const items = itemsSnapshot.docs.map(
            (doc) =>
              ({
                id: doc.id,
                ...doc.data(),
              }) as StockItem,
          );
          setStockItems(items);
          console.log("Loaded stock items:", items.length);
        } catch (error) {
          console.error("Error fetching stock items:", error);
        }
      };

      fetchStockItems();
    } else {
      setStockItems([]);
      setFilteredStockItems([]);
    }
  }, [useStock]);

  useEffect(() => {
    // Filter stock items based on search term
    const filtered = stockItems.filter(
      (item) =>
        item.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.itemCode.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredStockItems(filtered);
  }, [searchTerm, stockItems]);

  const handleOpenStockSelector = (lineIndex: number) => {
    setSelectedLineIndex(lineIndex);
    setSearchTerm("");
    setOpenStockSelector(true);
  };

  const handleSelectStockItem = (stockItem: StockItem) => {
    if (selectedLineIndex !== null) {
      // Concatenate item name with description if description exists
      const fullDescription = stockItem.description
        ? `${stockItem.itemName}\n${stockItem.description}`
        : stockItem.itemName;

      // Update all fields at once to avoid state closure issues
      const productLines = invoice.productLines.map((productLine, i) => {
        if (i === selectedLineIndex) {
          return {
            ...productLine,
            description: fullDescription,
            rate: stockItem.sellingPrice.toString(),
            unitOfMeasure: stockItem.unit,
            stockCode: stockItem.itemCode,
          };
        }
        return productLine;
      });

      setInvoice({ ...invoice, productLines });

      // Track this selection for visual feedback
      setSelectedStockPerLine({
        ...selectedStockPerLine,
        [selectedLineIndex]: stockItem,
      });
    }
    setOpenStockSelector(false);
    setSelectedLineIndex(null);
    setSearchTerm("");
  };

  const handleAddCustomColumn = () => {
    const columnName = prompt("Enter the column name:");
    if (columnName) {
      setCustomColumns([...customColumns, columnName]);
      setInvoice((prevInvoice) => ({
        ...prevInvoice,
        productLines: prevInvoice.productLines.map((line) => ({
          ...line,
          [columnName]: "",
        })),
      }));
    }
  };

  const handleDeleteCustomColumn = (columnName: string) => {
    setCustomColumns(customColumns.filter((col) => col !== columnName));
    setInvoice((prevInvoice) => ({
      ...prevInvoice,
      productLines: prevInvoice.productLines.map((line) => {
        const newLine = { ...line } as Partial<ProductLine> & {
          [key: string]: any;
        };
        delete newLine[columnName];
        return newLine as ProductLine;
      }),
    }));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputData(event.target.value);
  };

  const handleImportData = () => {
    const rows = inputData.split("\n").filter((row) => row.trim() !== "");
    const newProductLines = rows.map((row) => {
      const columns = row.split("\t");
      const newProductLine: ProductLine = { ...initialProductLine };
      columns.forEach((col, index) => {
        if (index === 0) newProductLine.description = col;
        if (index >= 1 && index <= customColumns.length) {
          newProductLine[customColumns[index - 1]] = col;
        }
        if (index === customColumns.length + 1) newProductLine.quantity = col;
        if (index === customColumns.length + 2) newProductLine.rate = col;
      });
      return newProductLine;
    });

    const currentIndex = 0; // Replace 0 with the actual index value
    setInvoice((prevInvoice) => {
      const productLines = [...prevInvoice.productLines];
      productLines.splice(currentIndex, 1, ...newProductLines);
      return { ...prevInvoice, productLines };
    });
    setOpenModal(false);
  };

  const dateFormat = "dd, MMM yyyy";

  // Try known formats explicitly instead of the ambiguous native Date parser,
  // which misreads DD/MM/YYYY dates (e.g. from a saved invoice) as MM/DD/YYYY
  const parseInvoiceDate = (value: string): Date | null => {
    if (!value) return null;
    for (const knownFormat of ["dd, MMM yyyy", "dd/MM/yyyy", "yyyy-MM-dd"]) {
      const parsed = parse(value, knownFormat, new Date());
      if (isValid(parsed)) return parsed;
    }
    const fallback = new Date(value);
    return isValid(fallback) ? fallback : null;
  };

  let invoiceDate;
  let invoiceDueDate;

  try {
    invoiceDate =
      invoice.invoiceDate !== ""
        ? parseInvoiceDate(invoice.invoiceDate)
        : new Date();
    if (!invoiceDate) throw new Error("Invalid invoiceDate");
    invoice.invoiceDate = format(invoiceDate, dateFormat);
  } catch (error) {
    console.error("Error parsing invoiceDate:", error);
    invoiceDate = new Date();
    invoice.invoiceDate = "";
  }

  try {
    invoiceDueDate =
      invoice.invoiceDueDate !== ""
        ? parseInvoiceDate(invoice.invoiceDueDate)
        : null;
    if (invoiceDueDate) {
      invoice.invoiceDueDate = format(invoiceDueDate, dateFormat);
    } else {
      invoice.invoiceDueDate = "";
    }
  } catch (error) {
    console.error("Error parsing invoiceDueDate:", error);
    invoice.invoiceDueDate = "";
  }

  const handleChange = (name: keyof Invoice, value: string | number) => {
    if (name !== "productLines") {
      const newInvoice = { ...invoice };

      if (name === "logoWidth" && typeof value === "number") {
        newInvoice[name] = value;
      } else if (name !== "logoWidth" && typeof value === "string") {
        newInvoice[name] = value;
      }

      setInvoice(newInvoice);
    }
  };

  const handleProductLineChange = (
    index: number,
    name: keyof ProductLine | string,
    value: string,
  ) => {
    const productLines = invoice.productLines.map((productLine, i) => {
      if (i === index) {
        const newProductLine = { ...productLine } as ProductLine;
        newProductLine[name] = value;
        return newProductLine;
      }
      return productLine;
    });

    setInvoice({ ...invoice, productLines });
  };

  const handleRemove = (i: number) => {
    const productLines = invoice.productLines.filter(
      (_: any, index: number) => index !== i,
    );

    setInvoice({ ...invoice, productLines });
  };

  const handleAdd = () => {
    const productLines = [...invoice.productLines, { ...initialProductLine }];

    setInvoice({ ...invoice, productLines });
  };
  const formatNumberWithCommas = (number: string) => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  const calculateAmount = (quantity: string, rate: string) => {
    const quantityNumber = parseFloat(quantity.replace(/[^\d.-]/g, ""));
    const rateNumber = parseFloat(rate.replace(/[^\d.-]/g, ""));
    const amount =
      quantityNumber && rateNumber ? quantityNumber * rateNumber : 0;

    return formatNumberWithCommas(amount.toFixed(2));
  };

  // Derived synchronously (not via effect) so PDF snapshots see correct totals immediately
  const subTotal = useMemo(() => {
    let total = 0;

    invoice.productLines.forEach((productLine: ProductLine) => {
      const quantityNumber = parseFloat(
        productLine.quantity.replace(/[^\d.-]/g, ""),
      );
      const rateNumber = parseFloat(productLine.rate.replace(/[^\d.-]/g, ""));
      const amount =
        quantityNumber && rateNumber ? quantityNumber * rateNumber : 0;

      total += amount;
    });

    return total;
  }, [invoice.productLines]);

  // Derived synchronously (not via effect) so PDF snapshots see columns like stockCode/unitOfMeasure immediately
  const derivedColumns = useMemo(() => {
    const columns = new Set<string>(customColumns);
    invoice.productLines.forEach((productLine: ProductLine) => {
      Object.keys(productLine).forEach((key) => {
        if (key !== "description" && key !== "quantity" && key !== "rate") {
          columns.add(key);
        }
      });
    });
    return Array.from(columns);
  }, [invoice.productLines, customColumns]);

  const saleTax = useMemo(() => {
    const match = invoice.taxLabel.match(/(\d+)%/);
    const taxRate = match ? parseFloat(match[1]) : 0;

    // If the tax rate is 0, leave it as 0
    return subTotal ? (subTotal * taxRate) / 100 : 0;
  }, [subTotal, invoice.taxLabel]);

  useEffect(() => {
    if (onChange) {
      onChange(invoice);
    }
  }, [onChange, invoice]);

  return (
    <Document pdfMode={pdfMode}>
      {!pdfMode && (
        <button onClick={() => setOpenModal(true)}>
          Paste Data from Spreadsheet
        </button>
      )}
      <Page className="invoice-wrapper" pdfMode={pdfMode}>
        {!pdfMode && (
          <Download
            data={invoice}
            setData={(d) => setInvoice(d)}
            isEditMode={isEditMode}
          />
        )}

        {/* MUI/Emotion components must never render into the react-pdf tree */}
        {!pdfMode && (
          <Modal open={openModal} onClose={() => setOpenModal(false)}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: 400,
                bgcolor: "background.paper",
                border: "2px solid #000",
                boxShadow: 24,
                p: 4,
              }}
            >
              <h2>Input Data</h2>
              <TextareaAutosize
                value={inputData}
                onChange={handleInputChange}
                placeholder="Paste your data here"
                minRows={10}
                style={{ width: "100%", marginBottom: "20px" }}
              />
              <Button variant="contained" onClick={handleImportData}>
                Import Data
              </Button>
            </Box>
          </Modal>
        )}

        {/* Stock Item Selector Modal */}
        {!pdfMode && useStock && (
          <Modal
            open={openStockSelector}
            onClose={() => setOpenStockSelector(false)}
            sx={{ zIndex: 1300 }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: "90%",
                maxWidth: 500,
                bgcolor: "background.paper",
                boxShadow: 24,
                p: 3,
                borderRadius: 2,
                maxHeight: "80vh",
                overflowY: "auto",
                zIndex: 1301,
              }}
            >
              <h2 style={{ marginTop: 0 }}>Select Stock Item</h2>

              <TextField
                fullWidth
                placeholder="Search by item name or code..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: "gray" }} />,
                }}
                sx={{ mb: 2 }}
              />

              {filteredStockItems.length === 0 ? (
                <Box sx={{ p: 2, textAlign: "center", color: "gray" }}>
                  No stock items found
                </Box>
              ) : (
                <List>
                  {filteredStockItems.map((item) => (
                    <Box key={item.id}>
                      <ListItemButton
                        onClick={() => handleSelectStockItem(item)}
                        sx={{
                          mb: 1,
                          border: "1px solid #eee",
                          borderRadius: 1,
                          "&:hover": {
                            backgroundColor: "#f5f5f5",
                          },
                        }}
                      >
                        <Box sx={{ display: "flex", gap: 2, width: "100%" }}>
                          {item.imageUrls && item.imageUrls.length > 0 && (
                            <img
                              src={item.imageUrls[0]}
                              alt={item.itemName}
                              style={{
                                width: 50,
                                height: 50,
                                objectFit: "cover",
                                borderRadius: 4,
                              }}
                            />
                          )}
                          <ListItemText
                            primary={`${item.itemCode} - ${item.itemName}`}
                            secondary={`Qty: ${item.quantity} | Price: ${item.sellingPrice.toFixed(2)}`}
                          />
                        </Box>
                      </ListItemButton>
                    </Box>
                  ))}
                </List>
              )}

              <Button
                fullWidth
                variant="outlined"
                onClick={() => setOpenStockSelector(false)}
                sx={{ mt: 2 }}
              >
                Close
              </Button>
            </Box>
          </Modal>
        )}

        <div style={{ outline: "none" }}>
          <View className="flex" pdfMode={pdfMode}>
            <View className="w-50" pdfMode={pdfMode}>
              <EditableFileImage
                className="logo"
                placeholder="Your Logo"
                value={invoice.logo}
                width={invoice.logoWidth}
                pdfMode={pdfMode}
                onChangeImage={(value) => handleChange("logo", value)}
                onChangeWidth={(value) => handleChange("logoWidth", value)}
              />
              <EditableInput
                className="fs-16 bold"
                placeholder="Your Company"
                value={invoice.companyName}
                onChange={(value) => handleChange("companyName", value)}
                pdfMode={pdfMode}
              />
              <EditableInput
                placeholder="Your Name"
                value={invoice.name}
                onChange={(value) => handleChange("name", value)}
                pdfMode={pdfMode}
              />
              <EditableInput
                placeholder="Company's Address"
                value={invoice.companyAddress}
                onChange={(value) => handleChange("companyAddress", value)}
                pdfMode={pdfMode}
              />

              <EditableInput
                placeholder="City, State Zip"
                value={invoice.companyAddress2}
                onChange={(value) => handleChange("companyAddress2", value)}
                pdfMode={pdfMode}
              />
              <EditableSelect
                options={countryList}
                value={invoice.companyCountry}
                onChange={(value) => handleChange("companyCountry", value)}
                pdfMode={pdfMode}
              />
              <EditableInput
                placeholder="Vat Number"
                value={invoice.companyVatNumber}
                onChange={(value) => handleChange("companyVatNumber", value)}
                pdfMode={pdfMode}
              />
            </View>
            <View className="w-50" pdfMode={pdfMode}>
              <EditableInput
                className="fs-20 right bold"
                placeholder="Invoice"
                value={invoice.title}
                onChange={(value) => handleChange("title", value)}
                pdfMode={pdfMode}
              />
            </View>
          </View>

          <View className="flex mt-10" pdfMode={pdfMode}>
            <View className="w-55" pdfMode={pdfMode}>
              <EditableInput
                className="bold dark mb-5"
                value={invoice.billTo}
                onChange={(value) => handleChange("billTo", value)}
                pdfMode={pdfMode}
              />
              <EditableInput
                placeholder="Your Client's Name"
                value={invoice.clientName}
                onChange={(value) => handleChange("clientName", value)}
                pdfMode={pdfMode}
              />
              <EditableInput
                placeholder="Client's Address"
                value={invoice.clientAddress}
                onChange={(value) => handleChange("clientAddress", value)}
                pdfMode={pdfMode}
              />

              <EditableInput
                placeholder="City, State Zip"
                value={invoice.clientAddress2}
                onChange={(value) => handleChange("clientAddress2", value)}
                pdfMode={pdfMode}
              />
              <EditableSelect
                options={countryList}
                value={invoice.clientCountry}
                onChange={(value) => handleChange("clientCountry", value)}
                pdfMode={pdfMode}
              />
              <EditableInput
                placeholder="Vat Number"
                value={invoice.clientVatNumber}
                onChange={(value) => handleChange("clientVatNumber", value)}
                pdfMode={pdfMode}
              />
            </View>
            <View className="w-45" pdfMode={pdfMode}>
              <View className="flex mb-5" pdfMode={pdfMode}>
                <View className="w-40" pdfMode={pdfMode}>
                  <EditableInput
                    className="bold"
                    value={invoice.invoiceTitleLabel}
                    onChange={(value) =>
                      handleChange("invoiceTitleLabel", value)
                    }
                    pdfMode={pdfMode}
                  />
                </View>
                <View className="w-60" pdfMode={pdfMode}>
                  <EditableInput
                    placeholder="INV-12"
                    value={invoice.invoiceTitle}
                    onChange={(value) => handleChange("invoiceTitle", value)}
                    pdfMode={pdfMode}
                  />
                </View>
              </View>
              <View className="flex mb-5" pdfMode={pdfMode}>
                <View className="w-40" pdfMode={pdfMode}>
                  <EditableInput
                    className="bold"
                    value={invoice.invoiceDateLabel}
                    onChange={(value) =>
                      handleChange("invoiceDateLabel", value)
                    }
                    pdfMode={pdfMode}
                  />
                </View>
                <View className="w-60" pdfMode={pdfMode}>
                  <EditableCalendarInput
                    value={format(invoiceDate, dateFormat)}
                    selected={invoiceDate}
                    onChange={(date) =>
                      handleChange(
                        "invoiceDate",
                        date && !Array.isArray(date)
                          ? format(date, dateFormat)
                          : "",
                      )
                    }
                    pdfMode={pdfMode}
                  />
                </View>
              </View>
              <View className="flex mb-5" pdfMode={pdfMode}>
                <View className="w-40" pdfMode={pdfMode}>
                  <EditableInput
                    className="bold"
                    value={invoice.invoiceDueDateLabel}
                    onChange={(value) =>
                      handleChange("invoiceDueDateLabel", value)
                    }
                    pdfMode={pdfMode}
                  />
                </View>
                <View className="w-60" pdfMode={pdfMode}>
                  <EditableCalendarInput
                    value={
                      invoiceDueDate ? format(invoiceDueDate, dateFormat) : ""
                    }
                    selected={invoiceDueDate}
                    onChange={(date) =>
                      handleChange(
                        "invoiceDueDate",
                        date
                          ? !Array.isArray(date)
                            ? format(date, dateFormat)
                            : ""
                          : "",
                      )
                    }
                    pdfMode={pdfMode}
                  />
                </View>
              </View>
            </View>
          </View>

          <View className="mt-10 bg-dark flex" pdfMode={pdfMode}>
            <View className="w-auto p-4-8" pdfMode={pdfMode}>
              <EditableInput
                className="white bold"
                value={invoice.productLineDescription}
                onChange={(value) =>
                  handleChange("productLineDescription", value)
                }
                pdfMode={pdfMode}
              />
            </View>
            {derivedColumns.map((column) => (
              <View key={column} className="w-auto p-4-8" pdfMode={pdfMode}>
                <EditableInput
                  className="white bold right"
                  placeholder={column}
                  value={column}
                  onChange={(value) => {
                    const newColumns = derivedColumns.map((col) =>
                      col === column ? value : col,
                    );
                    setCustomColumns(newColumns);
                  }}
                  pdfMode={pdfMode}
                />
                {!pdfMode && (
                  <button
                    className="link column__remove"
                    aria-label="Remove Column"
                    title="Remove Column"
                    onClick={() => handleDeleteCustomColumn(column)}
                  >
                    <span className="icon icon-remove bg-red"></span>
                  </button>
                )}
              </View>
            ))}
            <View className="w-17 p-4-8" pdfMode={pdfMode}>
              <EditableInput
                className="white bold right"
                value={invoice.productLineQuantity}
                onChange={(value) => handleChange("productLineQuantity", value)}
                pdfMode={pdfMode}
              />
            </View>
            <View className="w-17 p-4-8" pdfMode={pdfMode}>
              <EditableInput
                className="white bold right"
                value={invoice.productLineQuantityRate}
                onChange={(value) =>
                  handleChange("productLineQuantityRate", value)
                }
                pdfMode={pdfMode}
              />
            </View>
            <View className="w-18 p-4-8" pdfMode={pdfMode}>
              <EditableInput
                className="white bold right"
                value={invoice.productLineQuantityAmount}
                onChange={(value) =>
                  handleChange("productLineQuantityAmount", value)
                }
                pdfMode={pdfMode}
              />
            </View>
          </View>

          {invoice.productLines.map(
            (productLine: ProductLine, i: Key | null | undefined) => {
              return (
                <View key={i} className="row flex" pdfMode={pdfMode}>
                  <View className="w-auto p-4-8 pb-10" pdfMode={pdfMode}>
                    {pdfMode ? (
                      <EditableTextarea
                        className="dark"
                        rows={2}
                        placeholder="Enter item name/description"
                        value={productLine.description}
                        onChange={(value) =>
                          handleProductLineChange(
                            i as number,
                            "description",
                            value,
                          )
                        }
                        pdfMode={pdfMode}
                      />
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start",
                          flexWrap: "wrap",
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 200 }}>
                          <EditableTextarea
                            className="dark"
                            rows={2}
                            placeholder="Enter item name/description"
                            value={productLine.description}
                            onChange={(value) =>
                              handleProductLineChange(
                                i as number,
                                "description",
                                value,
                              )
                            }
                            pdfMode={pdfMode}
                          />
                        </Box>
                        {useStock && (
                          <Box
                            sx={{
                              display: "flex",
                              gap: 1,
                              alignItems: "center",
                              mt: 0.5,
                            }}
                          >
                            {selectedStockPerLine[i as number] && (
                              <Chip
                                label={`Stock: ${selectedStockPerLine[i as number]?.itemCode}`}
                                size="small"
                                onDelete={() =>
                                  handleOpenStockSelector(i as number)
                                }
                                onClick={() =>
                                  handleOpenStockSelector(i as number)
                                }
                                sx={{
                                  backgroundColor: "#c8e6c9",
                                  cursor: "pointer",
                                }}
                              />
                            )}
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleOpenStockSelector(i as number)
                              }
                              title={
                                selectedStockPerLine[i as number]
                                  ? "Change stock item"
                                  : "Select from stock"
                              }
                              sx={{
                                mt: 0.5,
                                bgcolor: "#f0f0f0",
                                "&:hover": { bgcolor: "#e0e0e0" },
                              }}
                            >
                              <SearchIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        )}
                      </Box>
                    )}
                  </View>
                  {derivedColumns.map((column) => (
                    <View
                      key={column}
                      className="w-auto p-4-8 pb-10"
                      pdfMode={pdfMode}
                    >
                      <EditableInput
                        className="dark right"
                        placeholder={column}
                        value={productLine[column] || ""}
                        onChange={(value) =>
                          handleProductLineChange(i as number, column, value)
                        }
                        pdfMode={pdfMode}
                      />
                    </View>
                  ))}
                  <View className="w-17 p-4-8 pb-10" pdfMode={pdfMode}>
                    <EditableInput
                      className="dark right"
                      value={productLine.quantity}
                      onChange={(value) =>
                        handleProductLineChange(i as number, "quantity", value)
                      }
                      pdfMode={pdfMode}
                    />
                  </View>
                  <View className="w-17 p-4-8 pb-10" pdfMode={pdfMode}>
                    <EditableInput
                      className="dark right"
                      value={productLine.rate}
                      onChange={(value) =>
                        handleProductLineChange(i as number, "rate", value)
                      }
                      pdfMode={pdfMode}
                    />
                  </View>
                  <View className="w-18 p-4-8 pb-10" pdfMode={pdfMode}>
                    <Text className="dark right" pdfMode={pdfMode}>
                      {calculateAmount(
                        productLine.quantity ?? "",
                        productLine.rate ?? "",
                      )}
                    </Text>
                  </View>

                  {!pdfMode && (
                    <button
                      className="link row__remove"
                      aria-label="Remove Row"
                      title="Remove Row"
                      onClick={() => handleRemove(i as number)}
                    >
                      <span className="icon icon-remove bg-red"></span>
                    </button>
                  )}
                </View>
              );
            },
          )}

          <View className="flex" pdfMode={pdfMode}>
            <View className="w-60 mt-10" pdfMode={pdfMode}>
              <View className="w-120 mt-10" pdfMode={pdfMode}>
                {" "}
                <EditableInput
                  className="bold "
                  value={invoice.extraLabel}
                  onChange={(value) => handleChange("extraLabel", value)}
                  pdfMode={pdfMode}
                />
                <EditableInput
                  value={invoice.extra}
                  onChange={(value) => handleChange("extra", value)}
                  pdfMode={pdfMode}
                />
              </View>
              {!pdfMode && (
                <button className="link" onClick={handleAdd}>
                  <span className="icon icon-add bg-green mr-10"></span>
                  Add Line Item
                </button>
              )}
              {!pdfMode && (
                <button className="link" onClick={handleAddCustomColumn}>
                  <span className="icon icon-add bg-green mr-10"></span>
                  Add Custom Column
                </button>
              )}
            </View>
            {/* <View className="w-50 mt-10" pdfMode={pdfMode}>
              <EditableInput
                className="bold "
                value={invoice.extraLabel}
                onChange={(value) => handleChange("extraLabel", value)}
                pdfMode={pdfMode}
              />
              <EditableInput
                value={invoice.extra}
                onChange={(value) => handleChange("extra", value)}
                pdfMode={pdfMode}
              />
            </View> */}

            <View className="w-40 mt-10" pdfMode={pdfMode}>
              <View className="flex" pdfMode={pdfMode}>
                <View className="w-40 p-5" pdfMode={pdfMode}>
                  <EditableInput
                    value={invoice.subTotalLabel}
                    onChange={(value) => handleChange("subTotalLabel", value)}
                    pdfMode={pdfMode}
                  />
                </View>
                <View className="w-60 p-5" pdfMode={pdfMode}>
                  <Text className="right bold dark" pdfMode={pdfMode}>
                    {subTotal
                      ? formatNumberWithCommas(subTotal.toFixed(2))
                      : "0.00"}
                  </Text>
                </View>
              </View>
              <View className="flex" pdfMode={pdfMode}>
                <View className="w-40 p-5" pdfMode={pdfMode}>
                  <EditableInput
                    value={invoice.taxLabel}
                    onChange={(value) => handleChange("taxLabel", value)}
                    pdfMode={pdfMode}
                  />
                </View>
                <View className="w-60 p-5" pdfMode={pdfMode}>
                  <Text className="right bold dark" pdfMode={pdfMode}>
                    {saleTax
                      ? formatNumberWithCommas(saleTax.toFixed(2))
                      : "0.00"}
                  </Text>
                </View>
              </View>
              <View className="flex bg-gray p-5" pdfMode={pdfMode}>
                <View className="w-40 p-5" pdfMode={pdfMode}>
                  <EditableInput
                    className="bold"
                    value={invoice.totalLabel}
                    onChange={(value) => handleChange("totalLabel", value)}
                    pdfMode={pdfMode}
                  />
                </View>
                <View className="w-60 p-5 flex " pdfMode={pdfMode}>
                  <EditableInput
                    className="dark bold right ml-30"
                    value={invoice.currency}
                    onChange={(value) => handleChange("currency", value)}
                    pdfMode={pdfMode}
                  />
                  <Text className="right bold dark" pdfMode={pdfMode}>
                    {formatNumberWithCommas(
                      (subTotal
                        ? saleTax
                          ? subTotal + saleTax
                          : subTotal
                        : 0
                      ).toFixed(2),
                    )}
                  </Text>
                </View>
              </View>
            </View>
          </View>

          <View className="mt-10" pdfMode={pdfMode}>
            <EditableInput
              className="bold w-100"
              value={invoice.notesLabel}
              onChange={(value) => handleChange("notesLabel", value)}
              pdfMode={pdfMode}
            />
            <EditableTextarea
              className="w-100"
              rows={2}
              value={invoice.notes}
              onChange={(value) => handleChange("notes", value)}
              pdfMode={pdfMode}
            />
          </View>
          <View className="mt-10" pdfMode={pdfMode}>
            <EditableInput
              className="bold w-100"
              value={invoice.termLabel}
              onChange={(value) => handleChange("termLabel", value)}
              pdfMode={pdfMode}
            />
            <EditableTextarea
              className="w-100"
              rows={2}
              value={invoice.term}
              onChange={(value) => handleChange("term", value)}
              pdfMode={pdfMode}
            />
          </View>
        </div>
      </Page>
    </Document>
  );
};

export default InvoicePage;
